import { CropListService } from "src/app/administrator/regulatory-models/crop-list/crop-list.service";
import { ActiveIngredientRate } from "src/app/shared/models/active-ingredient-rate";
import { ApplicationSchemeCropState, ApplicationSchemeXActiveIngredientRate, ApplicationSchemeXApplication } from "src/app/shared/models/application-scheme";
import { CropList } from "src/app/shared/models/crop-list";
import { Endpoint } from "src/app/shared/models/endpoint";
import { Gap } from "src/app/shared/models/gap";
import { Project } from "src/app/shared/models/project";
import { ProjectXCompoundXModel } from "src/app/shared/models/project-x-compound-x-model";
import { ProjectXModel } from "src/app/shared/models/project-x-model";
import { MacroGWInputs } from "src/app/shared/models/run/macro-gw-inputs";
import { PearlInputs } from "src/app/shared/models/run/pearl-inputs";
import { PelmoInputs } from "src/app/shared/models/run/pelmo-inputs";
import { SoilInputs } from "src/app/shared/models/run/soil-inputs";
import { Run } from "src/app/shared/models/run/run";
import { RunModel } from "src/app/shared/models/run/run-model";
import { Step1n2Inputs } from "src/app/shared/models/run/step1n2-inputs";
import { SwashInputs } from "src/app/shared/models/run/swash-inputs";
import { UKInputs } from "src/app/shared/models/run/uk-inputs";
import { Constants } from "src/app/shared/utils/constants";
import { Utils } from "src/app/shared/utils/utils";
import { EEAGapLogicService } from "../../eea-gap/eea-gap.logic.service";
import { EEAGapApiService } from "../../eea-gap/eea-gap.api.service";
import { Catalog } from "src/app/shared/models/echo/catalog";
import { FractionTransformedXEndpoint } from "src/app/shared/models/run/fraction-transformed-x-endpoint";
import * as moment from "moment";
import { Precursor } from "src/app/shared/models/echo/precursor";
import { SelectedProjectApiService } from "src/app/shared/components/selected-project/selected-project.api.service";
import { CropInterception } from "src/app/shared/models/crop-interception";
import { GroupedEndpointFieldValue } from "src/app/shared/models/grouped-endpoint-field-value";

export class RunModelsFactory {
    static cropListService: CropListService
    static gapLogicApi: EEAGapApiService
    static gapLogicService: EEAGapLogicService
    static geographiesList: Catalog[] = [];
    static cropInterceptions: CropInterception[] = [];
    static selectedProjectApi: SelectedProjectApiService;

    static createRunModels(runs: Run[], selectedProject: Project | undefined, userId: string, cropListService: CropListService, gapLogicApi: EEAGapApiService, gapLogicService: EEAGapLogicService, selectedProjectApiService: SelectedProjectApiService, overwritePreviousRuns: boolean = false, geographiesList?: Catalog[], cropInterceptions?: CropInterception[]): RunModel {
        this.cropListService = cropListService;
        this.gapLogicService = gapLogicService;
        this.geographiesList = geographiesList || [];
        this.cropInterceptions = cropInterceptions || [];
        this.gapLogicApi = gapLogicApi;
        this.selectedProjectApi = selectedProjectApiService;
        let runGroupedByModels = this.groupRunsByModel(runs);
        let runModels = this.creationRunModelsOrchestrator(runGroupedByModels, selectedProject, userId);
        runModels.overwritePreviousRuns = overwritePreviousRuns;
        return runModels;
    }

    static createRunModelsForPWC(runs: Run[], selectedProject: Project | undefined, userId: string, selectedProjectApiService: SelectedProjectApiService, overwritePreviousRuns: boolean = false): RunModel {
        this.selectedProjectApi = selectedProjectApiService;
        let runGroupedByModels = this.groupRunsByModel(runs);
        let runModels = this.creationRunModelsOrchestrator(runGroupedByModels, selectedProject, userId);
        runModels.overwritePreviousRuns = overwritePreviousRuns;
        return runModels;
    }

    static creationRunModelsOrchestrator(runGroupedByModels: { [key: string]: Run[]; }, selectedProject: Project | undefined, userId: string): RunModel {
        let runModels: RunModel = {};
        runModels.projectXModel = [];
        Object.entries(runGroupedByModels).forEach(async ([ModelName, runs]) => {
            if (runs[0] && runs[0]?.model?.modelPk && this.includeModelToBeRun(runs[0]?.model?.name)) {
                runModels.projectXModel?.push(this.createProjectXModelObject(runs, selectedProject, userId))
            }

            switch (ModelName) {
                case Constants.MODELS.STEP_1_2: {
                    runModels.step1n2Inputs = this.createStep1n2Object(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.SWASH: {
                    this.calculateRateMetaboliteAsParent(runs);
                    runModels.swash = this.createSwashObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.SWASH63: {
                    this.calculateRateMetaboliteAsParent(runs);
                    runModels.swash63 = this.createSwashObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.MACRO_GW: {
                    this.calculateRateMetaboliteAsParent(runs);
                    runModels.macroGW = this.createMacroGWObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.PELMO: {
                    this.calculateRateMetaboliteAsParent(runs);
                    runModels.pelmo = this.createPelmoObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.PEARL: {
                    this.calculateRateMetaboliteAsParent(runs);
                    runModels.pearl = this.createPearlObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.UK: {
                    runModels.ukTier = await this.createUKObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.GERMAN_SOIL: {
                    runModels.germanSoil = await this.createSoilObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.EU_SOIL: {
                    runModels.euSoil = await this.createSoilObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.NEU_SOIL: {
                    runModels.neuSoil = await this.createSoilObject(runs, selectedProject);
                    break;
                }
                case Constants.MODELS.SWAN: {
                    this.calculateRateMetaboliteAsParent(runs);
                    break;
                }
                default: {
                    break;
                }
            }
        });
        return runModels;
    }

    static createPelmoObject(runs: Run[], selectedProject: Project | undefined): PelmoInputs[] {
        var pelmoInputs: PelmoInputs[] = [];
        runs.forEach(run => {
            let alternativeCompoundName = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.PELMO, run.dataSet?.metabolitePk);
            let pelmoInput = {
                isParent: true,
                runID: run.id,
                activeSubstance: alternativeCompoundName ?? run.substanceName,
                substanceName: alternativeCompoundName ?? run.substanceName!,
                compound: alternativeCompoundName ?? run.substanceName,
                applicationScheme: run.applicationSchemeData?.name,
                crop: run.cropInterceptionName,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                pathway: run.dataSet?.description,
                degradationDT50: run.halfLifeInSoil,
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate),
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate),
                repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
                koc: run.kfoc,
                numberOfApplications: run.applicationSchemeData.numberOfApplications,
                season: run.applicationSchemeData?.season,
                location: run.applicationSchemeData?.location,
                locationCode: Constants.locationCodes[run.applicationSchemeData?.location],
                applicationDayOfYearString: [""],
                applicationType: run.applicationSchemeData?.applicationMethod,
                exponent: run.exponent,
                dateType: run.applicationSchemeData.dateType,
                plantUptake: run.coefficientForUptakeByPlant ?? 0,
                rainfallFile: "",
                sorptionValue: run.kfoc,
                molecularWeight: run.molecularWeight,
                vapourPressure: run.saturatedVapourPressure,
                DT50Sediment: run.halfLifeinSediment,
                DT50Water: run.halfLifeInWater,
                waterSolubility: run.solubilityInWater,
                soilDepthIncorporated: run.soilDepthIncorporated,
                gaps: this.getGapsByRun(run),
                model: run.model?.name!,
                soilDepth: run.applicationSchemeData.soilDepth,
                upperSoilDepth: 0,
                cropEvent: run.applicationSchemeData.cropEvent,
                vaporPressure: run.saturatedVapourPressure!,
                solubility: run.solubilityInWater!,
                fne: run.fne!,
                metabolites: this.getPelmoMetabolitesByRun(run, selectedProject),
                applicationDateType: run.applicationSchemeData.dateType,
                letter: "PA",
                metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(run),
            } as PelmoInputs;
            pelmoInputs.push(pelmoInput);
            pelmoInputs[pelmoInputs.length - 1].metabolites?.unshift(JSON.parse(JSON.stringify(pelmoInputs[pelmoInputs.length - 1])))
        });
        return pelmoInputs;
    }

    static getPelmoMetabolitesByRun(run: Run, selectedProject: Project | undefined): PelmoInputs[] {
        var pelmoInputs: PelmoInputs[] = [];
        let alternativeCompoundName = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.PELMO);
        run.metabolites.forEach((metabolite: any) => {
            let alternativeCompoundNameMetabolite = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.PELMO, metabolite.metabolitePk);
            let pelmoMetInput = {
                isParent: false,
                runID: run.id,
                activeSubstance: alternativeCompoundName ?? metabolite.substanceName,
                substanceName: alternativeCompoundNameMetabolite ?? metabolite.substanceName!,
                compound: alternativeCompoundNameMetabolite ?? metabolite.substanceName,
                applicationScheme: run.applicationSchemeData?.name,
                crop: run.cropInterceptionName,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                pathway: run.dataSet?.description,
                degradationDT50: metabolite.halfLifeInSoil,
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate),
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate),
                repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
                koc: metabolite.kfoc,
                numberOfApplications: run.applicationSchemeData.numberOfApplications,
                season: run.applicationSchemeData.season,
                location: run.applicationSchemeData?.location,
                locationCode: Constants.locationCodes[run.applicationSchemeData?.location],
                applicationDayOfYearString: [""],
                applicationType: run.applicationSchemeData?.applicationMethod,
                exponent: metabolite.exponent,
                dateType: run.applicationSchemeData.dateType,
                plantUptake: metabolite.coefficientForUptakeByPlant ?? 0,
                rainfallFile: "",
                sorptionValue: metabolite.kfoc,
                molecularWeight: metabolite.molecularWeight,
                vapourPressure: metabolite.saturatedVapourPressure,
                DT50Sediment: metabolite.halfLifeinSediment,
                DT50Water: metabolite.halfLifeInWater,
                waterSolubility: metabolite.solubilityInWater,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                gaps: this.getGapsByRun(run),
                model: run.model?.name!,
                soilDepth: run.applicationSchemeData.soilDepth,
                upperSoilDepth: 0,
                cropEvent: run.applicationSchemeData.cropEvent,
                vaporPressure: metabolite.saturatedVapourPressure!,
                solubility: metabolite.solubilityInWater!,
                fne: metabolite.fne!,
                metabolites: [],
                precursors: this.processPrecursors(metabolite.precursors, selectedProject?.projectXCompoundXModel),
                applicationDateType: run.applicationSchemeData.dateType,
                letter: metabolite.pelmoLetter,
                metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(metabolite),
            } as PelmoInputs;
            pelmoInputs.push(pelmoMetInput);
        });
        return pelmoInputs;
    }

    static createPearlObject(runs: Run[], selectedProject: Project | undefined): PearlInputs[] {
        var pearlInputs: PearlInputs[] = [];
        runs.forEach(run => {
            let alternativeCompoundName = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.PEARL, run.dataSet?.metabolitePk);
            pearlInputs.push({
                runID: Utils.trimRunID(run.id!),
                substanceName: alternativeCompoundName ?? run.substanceName,
                activeSubstance: alternativeCompoundName ?? run.substanceName,
                applicationMethod: run.applicationSchemeData?.applicationMethod,
                applicationRate: this.getRunRate(run),
                applicationScheme: run.applicationSchemeData?.name,
                coefficientForUptakeByPlant: run.coefficientForUptakeByPlant ?? 0,
                crop: run.cropInterceptionName,
                cropEvent: run.applicationSchemeData.cropEvent,
                dateType: run.applicationSchemeData.dateType,
                desorptionRate: run.kdes,
                degradationDT50: run.halfLifeInSoil,
                exponent: run.exponent,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                fractionNonEquilibrium: run.fne,
                gaps: this.getGapsByRun(run),
                model: run.model?.name!,
                kfoc: run.kfoc,
                location: run.applicationSchemeData?.location,
                metabolites: this.getPearlMetabolitesByRun(run, selectedProject),
                molecularWeight: run.molecularWeight,
                numberOfApplications: run.applicationSchemeData?.numberOfApplications,
                pathway: run.dataSet?.description,
                repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
                waterSolubility: run.solubilityInWater,
                vapourPressure: run.saturatedVapourPressure,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                fractionInSoil: undefined,
                metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(run),
            } as PearlInputs);
            pearlInputs[pearlInputs.length - 1].metabolites?.unshift(JSON.parse(JSON.stringify(pearlInputs[pearlInputs.length - 1])))
        });
        return pearlInputs;
    }

    static getPearlMetabolitesByRun(run: Run, selectedProject: Project | undefined): PearlInputs[] {
        var pearlInputs: PearlInputs[] = [];
        run.metabolites.forEach((metabolite: any) => {
            let alternativeCompoundNameMetabolite = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.PEARL, metabolite.metabolitePk);
            pearlInputs.push({
                activeSubstance: alternativeCompoundNameMetabolite ?? metabolite.substanceName,
                substanceName: alternativeCompoundNameMetabolite ?? metabolite.substanceName,
                applicationMethod: run.applicationSchemeData?.applicationMethod,
                applicationScheme: run.applicationSchemeData?.name,
                coefficientForUptakeByPlant: metabolite.coefficientForUptakeByPlant ?? 0,
                crop: run.cropInterceptionName,
                cropEvent: run.applicationSchemeData.cropEvent,
                dateType: run.applicationSchemeData.dateType,
                desorptionRate: metabolite.kdes,
                degradationDT50: metabolite.halfLifeInSoil,
                exponent: metabolite.exponent,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                fractionNonEquilibrium: metabolite.fne,
                gaps: this.getGapsByRun(run),
                model: run.model?.name!,
                kfoc: metabolite.kfoc,
                location: run.applicationSchemeData?.location,
                metabolites: [],
                molecularWeight: metabolite.molecularWeight,
                numberOfApplications: run.applicationSchemeData?.numberOfApplications,
                pathway: run.dataSet?.description,
                repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
                runID: Utils.trimRunID(run.id!),
                waterSolubility: metabolite.solubilityInWater,
                vapourPressure: metabolite.saturatedVapourPressure,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                fractionInSoil: this.getFormationFractionByKey(metabolite.precursors, Constants.FIELD_NAMES.FRACTION_IN_SOIL, selectedProject),
                metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(metabolite),
            } as PearlInputs);
        });
        return pearlInputs;
    }

    static createMacroGWObject(runs: Run[], selectedProject: Project | undefined): MacroGWInputs[] {
        let macroGWInputs: MacroGWInputs[] = [];
        runs.forEach(run => {
            let alternativeCompoundName = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.MACRO_GW, run.dataSet?.metabolitePk);
            const dosages = this.getDosages(run);
            macroGWInputs.push({
                runID: run.id,
                isParent: true,
                repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
                substanceName: alternativeCompoundName ?? run.substanceName,
                activeSubstance: alternativeCompoundName ?? run.substanceName, 
                compound: alternativeCompoundName ?? run.substanceName,
                applicationScheme: run.applicationSchemeData?.name,
                applicationType: run.applicationSchemeData?.applicationMethod, 
                crop: run.cropInterceptionName,
                pathway: run.dataSet?.description,
                location: run.applicationSchemeData?.location,
                locationCode: Constants.locationCodes[run.applicationSchemeData?.location],
                commonSubstanceName: run.substanceName,
                degradationDT50: run.halfLifeInSoil,
                exponent: run.exponent,
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate), 
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation), 
                koc: run.kfoc, 
                sorption: run.kfoc, 
                sorptionValue: run.kfoc,
                vapourPressure: run.saturatedVapourPressure,
                waterSolubility: run.solubilityInWater,
                plantUptake: run.coefficientForUptakeByPlant ?? 0,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(run),
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate), 
                parentAppliedDose: dosages,
                appliedDose: dosages,
                metabolites: this.getMacroGWMetabolitesByRun(run, selectedProject),
                molecularWeight: run.molecularWeight, 
                gaps: this.getMacroGwGapsByRun(run),
                model: run.model?.name,
                numberOfApplications: run.applicationSchemeData.numberOfApplications, 
            } as MacroGWInputs);
        });
        return macroGWInputs;
    }

    static buildMacroGWParentMirror(run: Run, selectedProject: Project | undefined): MacroGWInputs {
        const alternativeCompoundName = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.MACRO_GW, run.dataSet?.metabolitePk);
        return {
            runID: run.id,
            isParent: true,
            repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
            substanceName: alternativeCompoundName ?? run.substanceName,
            compound: alternativeCompoundName ?? run.substanceName,
            applicationScheme: run.applicationSchemeData?.name,
            crop: run.cropInterceptionName,
            formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
            pathway: run.dataSet?.description,
            molecularWeight: run.molecularWeight,
            degradationDT50: run.halfLifeInSoil,
            exponent: run.exponent,
            sorptionValue: run.kfoc,
            vapourPressure: run.saturatedVapourPressure,
            waterSolubility: run.solubilityInWater,
            plantUptake: run.coefficientForUptakeByPlant ?? 0,
            rainfallFile: "", 
            season: run.applicationSchemeData.season, 
            soilDepthIncorporated: run.applicationSchemeData.soilDepth, 
            numberOfApplications: run.applicationSchemeData.numberOfApplications,
            metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(run),
            metabolites: [],
            precursors: [],
            gaps: [],
            model: run.model?.name,
        } as MacroGWInputs;
    }

    static getMacroGWMetabolitesByRun(run: Run, selectedProject: Project | undefined): MacroGWInputs[] {
        const macroGWInputsArray: MacroGWInputs[] = [];
        macroGWInputsArray.push(this.buildMacroGWParentMirror(run, selectedProject));
        run.metabolites.forEach((metabolite: Endpoint) => {
            const alternativeCompoundNameMetabolite = Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.MACRO_GW, metabolite.metabolitePk);
            macroGWInputsArray.push({
                runID: run.id,
                isParent: false,
                repeatInterval: run.applicationSchemeData?.repeatInterval ?? 0,
                activeSubstance: Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, Constants.MODELS.MACRO_GW, run.dataSet?.metabolitePk) ?? run.substanceName,
                substanceName: alternativeCompoundNameMetabolite ?? metabolite.substanceName,
                compound: alternativeCompoundNameMetabolite ?? metabolite.substanceName,
                applicationScheme: run.applicationSchemeData?.name,
                applicationType: run.applicationSchemeData?.applicationMethod, 
                crop: run.globalCropName,
                cropPk: run.globalCropPk, 
                degradationDT50: metabolite.halfLifeInSoil, 
                exponent: metabolite.exponent, 
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate), 
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                koc: metabolite.kfoc, 
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate), 
                metabolites: [], 
                molecularWeight: metabolite.molecularWeight, 
                numberOfApplications: run.applicationSchemeData.numberOfApplications, 
                pathway: run.dataSet?.description,
                plantUptake: metabolite.coefficientForUptakeByPlant ?? 0, 
                rainfallFile: "", 
                season: run.applicationSchemeData.season, 
                sorptionValue: metabolite.kfoc,
                vapourPressure: metabolite.saturatedVapourPressure,
                waterSolubility: metabolite.solubilityInWater,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                metaboliteAppliedFactor: this.getMetaboliteAppliedFactor(metabolite),
                precursors: this.buildMacroGWPrecursors(metabolite, run, selectedProject),
                gaps: [],
                model: run.model?.name,
            } as MacroGWInputs);
        });
        return macroGWInputsArray;
    }

    static getMetaboliteAppliedFactor(source: { groupedEndpointFieldValues?: GroupedEndpointFieldValue[] | any }): number | undefined {
        const entry = source?.groupedEndpointFieldValues?.find(
            (f: GroupedEndpointFieldValue) => f.key?.toLowerCase() === Constants.DATA_VALUES_NAMES.metaboliteAppliedRateFactor
        );
        return entry?.value;
    }

    static buildMacroGWPrecursors(metabolite: Endpoint, run: Run, selectedProject: Project | undefined): any[] {
        if (!metabolite.precursors) return [];
        return metabolite.precursors.map((p: Precursor) => {
            const altName = this.getAlternativeName(p, selectedProject?.projectXCompoundXModel);
            const substanceName = altName ?? p.substanceName;
            const precursorMW = this.getMacroGWPrecursorMolecularWeight(p, run);
            const molecularWeightRatio = (metabolite.molecularWeight && precursorMW)
                ? metabolite.molecularWeight / precursorMW
                : undefined;
            return {
                substanceName,
                formationFraction: p.formationFraction,
                precursorType: p.precursorType,
                molecularWeightRatio,
            };
        });
    }

    static getMacroGWPrecursorMolecularWeight(precursor: Precursor, run: Run): number | undefined {
        if (precursor.metabolitePk == null && precursor.moleculePk === run.activeIngredientPk) {
            return run.molecularWeight;
        }
        const match = run.metabolites?.find((m: Endpoint) =>
            (precursor.metabolitePk != null && m.metabolitePk === precursor.metabolitePk) ||
            (precursor.substanceName != null && m.substanceName === precursor.substanceName)
        );
        return match?.molecularWeight ?? run.molecularWeight;
    }

    static processPrecursors(precursors: Precursor[], projectXCompoundXModel: ProjectXCompoundXModel[] | undefined) {
        precursors.forEach((precursor: Precursor) => {
            let alternativeNameCompound = this.getAlternativeName(precursor, projectXCompoundXModel);
            if (alternativeNameCompound)
                precursor.substanceName = alternativeNameCompound;
        });
        return precursors;
    }

    static getAlternativeName(precursor: Precursor, projectXCompoundXModel: ProjectXCompoundXModel[] | undefined): string | undefined {
        let alternativeNameCompound: string | undefined;
        if (precursor.metabolitePk != null) {
            let compound = projectXCompoundXModel?.find(
                (x: ProjectXCompoundXModel) => x.MetabolitePk === precursor.metabolitePk
            );
            if (compound) return compound.AlternativeNameCompound;
        }
        if (!alternativeNameCompound && precursor.moleculePk != null) {
            let compound = projectXCompoundXModel?.find(
                (x: ProjectXCompoundXModel) => x.MoleculePk === precursor.moleculePk && x.MetabolitePk == null
            )
            if (compound) return compound.AlternativeNameCompound;
        }
        return alternativeNameCompound;
    }

    static getPearlAlternativeName(precursor: FractionTransformedXEndpoint, projectXCompoundXModel: ProjectXCompoundXModel[] | undefined): string | undefined {
        let alternativeNameCompound: string | undefined;
        if (precursor.metabolitePk != null) {
            let compound = projectXCompoundXModel?.find(
                (x: ProjectXCompoundXModel) => x.MetabolitePk === precursor.metabolitePk
            );
            if (compound) return compound.AlternativeNameCompound ?? precursor.substanceName;
        }
        if (!alternativeNameCompound && precursor.moleculePk != null) {
            let compound = projectXCompoundXModel?.find(
                (x: ProjectXCompoundXModel) => x.MoleculePk === precursor.moleculePk && x.MetabolitePk == null
            )
            if (compound) return compound.AlternativeNameCompound ?? precursor.substanceName;
        }
        return precursor.substanceName;
    }

    static getRunRate(run: Run): number | undefined {
        if (!run.applicationSchemeData.hasVariableRates) {
            return run.applicationSchemeData.applicationSchemeXActiveIngredientRate?.find((ai: ApplicationSchemeXActiveIngredientRate) => ai.moleculePk === run.activeIngredientPk)?.rate;
        }
        let firstApplication = run.applicationSchemeData.applicationSchemeXApplication?.find((application: ApplicationSchemeXApplication) => application.application_number == 1);
        return firstApplication?.applicationSchemeXActiveIngredientRate?.find((ai: ApplicationSchemeXActiveIngredientRate) => ai.moleculePk === run.activeIngredientPk)?.rate;
    }

    static getMacroGwGapsByRun(run: Run): Gap[] {
        return run.applicationSchemeData?.hasVariableRates ? this.getMacroGapsByRunWithVariableRates(run) : this.getMacroGapsByRunWithoutVariableRates(run)
    }

    static getMacroGapsByRunWithoutVariableRates(run: Run): Gap[] {
        const rate = run.applicationSchemeData.applicationSchemeXActiveIngredientRate?.find(
            (ai: ApplicationSchemeXActiveIngredientRate) => ai.moleculePk === run.activeIngredientPk
        )?.rate;
        let gap: Gap = {
            activeIngredientRates: [] = [],
            gapPk: 0,
            applicationSchemePk: 0,
            cropInterception: 0
        };
        gap.applicationDate = Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate);
        gap.cropGrowthStageInit = run.bbchEarliest;
        gap.cropGrowthStageFinal = run.bbchEarliest;
        gap.applicationIntervalMinPk = run.applicationSchemeData.applicationInterval;
        gap.interval = run.applicationSchemeData.applicationInterval;
        gap.dateType = Constants.DATE_TYPE_VALUES.ABSOLUTE;
        gap.cropInterception = run.applicationSchemeData.cropInterception;
        gap.bbchRange = run.bbchRange;
        gap.minBBCH = this.parseMinBBCH(run.bbchRange, run.applicationSchemeData.bbchEarliest);
        gap.bbch = run.applicationSchemeData.bbchEarliest != null ? String(run.applicationSchemeData.bbchEarliest) : undefined;
        gap.soilDepthIncorporated = run.applicationSchemeData.soilDepth ?? 0;
        gap.rate = rate;
        gap.dosage = rate;
        let ais: ActiveIngredientRate[] = []
        run.applicationSchemeData.applicationSchemeXActiveIngredientRate.filter((ai: ApplicationSchemeXActiveIngredientRate) => run.activeIngredientPk == ai.moleculePk).forEach((AI: ApplicationSchemeXActiveIngredientRate) => {
            ais.push({
                activeIngredientRateXGapPk: 0,
                activeIngredientPk: AI.moleculePk,
                max: AI.rate,
                activeIngredientName: AI.substanceName,
            });
        });
        gap.activeIngredientRates = ais;
        let gaps: Gap[] = [gap];
        let interval = 0;
        for (let i = 1; i < run.applicationSchemeData.numberOfApplications; i++) {
            let otherGap: Gap = JSON.parse(JSON.stringify(gap))
            interval += run.applicationSchemeData.applicationInterval;
            otherGap.applicationDate = Utils.addDaysToDateFormated(gap.applicationDate, interval);
            gaps.push(otherGap);
        }
        return gaps;
    }

    static getMacroGapsByRunWithVariableRates(run: Run): Gap[] {
        let gaps: Gap[] = [];
        let interval = 0;
        let applications = run.applicationSchemeData?.applicationSchemeXApplication?.sort((a: any, b: any) => {
            return a.application_number - b.application_number;
        }) ?? [];
        applications.forEach((application: any) => {
            const appRate = application.applicationSchemeXActiveIngredientRate?.find(
                (ai: ApplicationSchemeXActiveIngredientRate) => ai.moleculePk === run.activeIngredientPk
            )?.rate;
            let gap: Gap = {
                activeIngredientRates: [] = [],
                gapPk: 0,
                applicationSchemePk: 0,
                cropInterception: 0
            };
            gap.applicationDate = Utils.formatIsoDateToCustom(application.applicationSchemeXApplicationWindow[0]?.firstDate);
            gap.cropGrowthStageInit = application.earliest_bbch;
            gap.cropGrowthStageFinal = application.earliest_bbch;
            gap.applicationIntervalMinPk = interval + application.min_application_interval;
            gap.interval = application.min_application_interval;
            gap.dateType = Constants.DATE_TYPE_VALUES.ABSOLUTE;
            gap.cropInterception = application.crop_interception;
            gap.bbchRange = application.bbchRange;
            gap.minBBCH = this.parseMinBBCH(application.bbchRange, application.earliest_bbch);
            gap.bbch = application.earliest_bbch != null ? String(application.earliest_bbch) : undefined;
            gap.soilDepthIncorporated = run.applicationSchemeData.soilDepth ?? 0;
            gap.rate = appRate;
            gap.dosage = appRate;
            let ais: ActiveIngredientRate[] = []
            application.applicationSchemeXActiveIngredientRate.filter((ai: ApplicationSchemeXActiveIngredientRate) => run.activeIngredientPk == ai.moleculePk).forEach((AI: ApplicationSchemeXActiveIngredientRate) => {
                if (AI.moleculePk === run.activeIngredientPk) {
                    ais.push({
                        activeIngredientRateXGapPk: 0,
                        activeIngredientPk: AI.moleculePk,
                        max: AI.rate,
                        activeIngredientName: AI.substanceName,
                    });
                }
            });
            gap.activeIngredientRates = ais;
            gaps.push(gap);
            interval += application.min_application_interval;
        });
        return gaps;
    }

    static parseMinBBCH(bbchRange: string | null | undefined, fallback: any): number | undefined {
        if (bbchRange) {
            const match = /^\s*(\d+)/.exec(bbchRange);
            if (match) return Number(match[1]);
        }
        const n = Number(fallback);
        return isNaN(n) ? undefined : n;
    }

    static includeModelToBeRun(model: string): boolean {
        let validModels = Object.values(Constants.MODELS).filter((modelName: string) => modelName !== Constants.MODELS.SWAN);
        return validModels.includes(model);
    }

    static createProjectXModelObject(runs: Run[], selectedProject: Project | undefined, userId: string): ProjectXModel {
        if (selectedProject) {
            selectedProject.status = Constants.PROJECT_STATUS_ID.IN_QUEUE;
        }
        return {
            projectPk: selectedProject?.projectPk,
            modelPk: runs[0]?.model?.modelPk,
            active: true,
            status: Constants.PROJECT_STATUS_ID.IN_QUEUE,
            lastOutputFolder: runs[0]?.id?.split("_")[0],
            runBy: userId,
            runStartDate: moment().format(),
            feature: this.selectedProjectApi.featureAcronym
        } as ProjectXModel;
    }

    static createSwashObject(runs: Run[], selectedProject: Project | undefined): SwashInputs[] {
        let swashInputs: SwashInputs[] = [];
        let runSwanModel = selectedProject!.projectXCompoundXModel?.some((x: ProjectXCompoundXModel) => x.ModelName === Constants.MODELS.SWAN);
        runs.forEach(run => {
            swashInputs.push({
                activeSubstance: Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, run.model?.name, run.dataSet?.metabolitePk) ?? Utils.repalceIllegalCharactersInSubstanceName(run.substanceName!),
                applicationDayOfYearString: [""],
                applicationScheme: run.applicationSchemeData?.name,
                applicationType: run.applicationSchemeData.applicationMethod,
                bbchEarliest: run.applicationSchemeData?.bbchEarliest,
                chemicalApplicationMethod: run.applicationSchemeData.chemicalApplicationMethod,
                compound: "",
                cropDriftInterceptionPk: run.applicationSchemeData.cropDriftInterceptionPk,
                cropEVAName: run.cropEVAName,
                cropName: run.cropDriftInterceptionName,
                cropPk: run.globalCropPk,
                degradationDT50: run.halfLifeInSoil,
                deposition: run.applicationSchemeData.deposition,
                dosages: this.getDosages(run),
                DT50Sediment: run.halfLifeinSediment,
                DT50Water: run.halfLifeInWater,
                exponent: run.exponent,
                extraFractionInSediment: undefined,
                extraFractionInSoil: undefined,
                extraFractionInSurfaceWater: undefined,
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate),
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                fractionInSediment: undefined,
                fractionInSoil: undefined,
                fractionInSurfaceWater: undefined,
                fullScenario: run.applicationSchemeData.fullScenario,
                interval: run.applicationSchemeData?.applicationInterval,
                intervals: this.returnsListValuesRepeatedByNumOfApplications(run.applicationSchemeData.numberOfApplications, run.applicationSchemeData?.applicationInterval),
                applicationIntervals: this.getSwashApplicationIntervals(run),
                appTimingDirection: run.applicationSchemeData?.appTimingDirection,
                kfoc: run.kfoc,
                kfEnable: run.kfEnable,
                kfValue: run.kfValue,
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate),
                metabolite: "",
                metabolites: this.getSwashMetabolitesByRun(run, selectedProject).sort((a, b) => (a.compound < b.compound ? -1 : 1)),
                modelVersion: run.model?.version,
                molecularMassActive: run.molecularWeight,
                nozzleDriftReduction10m: run.applicationSchemeData.nozzleDriftReduction10m ?? 0,
                nozzleDriftReduction20m: run.applicationSchemeData.nozzleDriftReduction20m ?? 0,
                numberOfApplications: run.applicationSchemeData.numberOfApplications,
                pathway: run.dataSet?.description,
                plantUptake: run.coefficientForUptakeByPlant ?? 0,
                rainfallFile: "",
                runID: run.id,
                runOff: run.applicationSchemeData.runOff,
                runSwanModel,
                scenario: run.applicationSchemeData.location,
                season: run.applicationSchemeData.season,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                sorptionValue: 0,
                sprayDrift: run.applicationSchemeData.sprayDrift,
                swashCropInterception: run.applicationSchemeData?.swashCropInterception,
                swanCropInterception: run.applicationSchemeData?.swanCropInterception,
                useSwashCropInterception: run.applicationSchemeData?.useSwashCropInterception,
                useSwanCropInterception: run.applicationSchemeData?.useSwanCropInterception,
                vapourPressure: run.saturatedVapourPressure,
                vapourPressureTemperature: run.saturatedVapourPressureTemperature,
                waterSolubility: run.solubilityInWater,
                waterSolubilityTemperature: run.solubilityInWaterTemperature,
                waterType: run.applicationSchemeData.waterType,
                mitigationsXApplicationScheme: run.mitigationsXApplicationScheme,
                cropInterceptionPercentage: run.applicationSchemeData?.cropInterception,
            } as SwashInputs);
        });
        return swashInputs;
    }

    static getSwashMetabolitesByRun(run: any, selectedProject: Project | undefined): SwashInputs[] {
        var swashInputsArray: SwashInputs[] = [];
        run.metabolites.forEach((metabolite: any) => {
            metabolite.precursors = this.processPrecursors(metabolite.precursors, selectedProject?.projectXCompoundXModel);
            swashInputsArray.push({
                activeSubstance: Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, run.model?.name, run.dataSet?.metabolitePk) ?? Utils.repalceIllegalCharactersInSubstanceName(run.substanceName),
                applicationDayOfYearString: [""],
                applicationScheme: run.applicationSchemeData?.name,
                applicationType: run.applicationSchemeData.applicationMethod,
                chemicalApplicationMethod: run.applicationSchemeData.chemicalApplicationMethod,
                compound: Utils.getAlternativeCompoundName(run.activeIngredientPk, selectedProject, run.model?.name, metabolite.metabolitePk) ?? Utils.repalceIllegalCharactersInSubstanceName(metabolite.substanceName),
                cropDriftInterceptionPk: run.applicationSchemeData.cropDriftInterceptionPk,
                cropName: run.cropDriftInterceptionName,
                cropPk: run.globalCropPk,
                degradationDT50: metabolite.halfLifeInSoil,
                dosages: this.getDosages(run),
                DT50Sediment: metabolite.halfLifeinSediment,
                DT50Water: metabolite.halfLifeInWater,
                exponent: metabolite.exponent,
                extraFractionInSediment: { formationFraction: 0, precursor: "" },
                extraFractionInSoil: { formationFraction: 0, precursor: "" },
                extraFractionInSurfaceWater: { formationFraction: 0, precursor: "" },
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate),
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                fractionInSediment: this.getFormationFraction(metabolite.precursors, Constants.FIELD_NAMES.FRACTION_IN_SEDIMENT),
                fractionInSoil: this.getFormationFraction(metabolite.precursors, Constants.FIELD_NAMES.FRACTION_IN_SOIL),
                fractionInSurfaceWater: this.getFormationFraction(metabolite.precursors, Constants.FIELD_NAMES.FRACTION_IN_SURFACE_WATER),
                fullScenario: run.applicationSchemeData.fullScenario,
                interval: run.applicationSchemeData?.applicationInterval,
                intervals: this.returnsListValuesRepeatedByNumOfApplications(run.applicationSchemeData.numberOfApplications, run.applicationSchemeData?.applicationInterval),
                applicationIntervals: this.getSwashApplicationIntervals(run),
                appTimingDirection: run.applicationSchemeData?.appTimingDirection,
                kfoc: metabolite.kfoc,
                kfEnable: metabolite.kfEnable,
                kfValue: metabolite.kfValue,
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate),
                metabolite: "",
                metabolites: [],
                molecularMassActive: metabolite.molecularWeight,
                nozzleDriftReduction10m: run.applicationSchemeData.nozzleDriftReduction10m ?? 0,
                nozzleDriftReduction20m: run.applicationSchemeData.nozzleDriftReduction20m ?? 0,
                numberOfApplications: run.applicationSchemeData.numberOfApplications,
                pathway: run.dataSet?.description,
                plantUptake: metabolite.coefficientForUptakeByPlant ?? 0,
                rainfallFile: "",
                runID: run.id,
                runSwanModel: run.runSwanModel,
                scenario: run.applicationSchemeData.location,
                season: run.applicationSchemeData.season,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                sorptionValue: 0,
                swashCropInterception: run.applicationSchemeData?.swashCropInterception,
                swanCropInterception: run.applicationSchemeData?.swanCropInterception,
                useSwashCropInterception: run.applicationSchemeData?.useSwashCropInterception,
                vapourPressure: metabolite.saturatedVapourPressure,
                waterSolubility: metabolite.solubilityInWater,
                waterType: run.applicationSchemeData.waterType,
            } as SwashInputs);
        });
        return swashInputsArray;
    }

    static getFormationFraction(precursors: Precursor[], fractiopnName: string) {
        if (!precursors)
            return { formationFraction: 0, precursor: "" }

        var precursor = precursors.find(obj => obj.precursorType === fractiopnName);
        if (precursor !== undefined) {
            return {
                formationFraction: precursor.formationFraction,
                precursor: Utils.repalceIllegalCharactersInSubstanceName(precursor.substanceName)
            }
        }
        else { return { formationFraction: 0, precursor: "" } };
    }
    static createSoilObject(runs: Run[], selectedProject: Project | undefined): SoilInputs[] {
        var soilObjectArray: SoilInputs[] = [];
        runs.forEach(run => {
            var soilObjectInputs: SoilInputs = {
                jobID: run.id,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                crop: run.cropInterceptionName,
                applicationScheme: run.applicationSchemeData?.name,
                applicationRate: run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate,
                isParent: true,
                activeSubstance: run.substanceName,
                metabolite: run.substanceName,
                pathway: run.dataSet?.description,
                molecularWeight: run.molecularWeight ?? 0,
                parentMolecularWeight: run.molecularWeight ?? 0,
                maximumOccurance: run.maximumOccurrenceInSoil ?? 0,
                koc: run.kfoc ?? 0,
                degT50: run.halfLifeInSoil ?? 0,
                degT90: this.calculateDegT90(run.k1) ?? 0,
                k1: run.k1 ?? 0,
                k2: run.k2 ?? 0,
                alpha: run.alpha ?? 0,
                beta: run.beta ?? 0,
                tb: run.tb ?? 0,
                g: run.g ?? 0,
                models: run.kineticModel ?? "",
                cropInterception: run.applicationSchemeData.cropInterception ?? 0,
                tillage: run.applicationSchemeData.tillage,
                gaps: this.getGapsByRunWithoutVariableRates(run),
                numApplication: run.applicationSchemeData.numberOfApplications ?? 0,
                applicationInterval: run.applicationSchemeData.applicationInterval ?? 0,
                metabolites: this.getSoilMetabolitesByRun(run, selectedProject),
                bbch: run.applicationSchemeData.bbchEarliest ?? "",
                initialSoilDepth: run.applicationSchemeData.soilDepth ?? 0,
                plateauSoilDepth: run.applicationSchemeData.plateauSoilDepth ?? 0,
                simulationYear: run.applicationSchemeData.simulationYear ?? 0,
                model: run.model?.name,
                bbchRange: run.bbchRange ?? "",
            };
            soilObjectArray.push(soilObjectInputs);
            soilObjectArray[soilObjectArray.length - 1].metabolites?.unshift(JSON.parse(JSON.stringify(soilObjectArray[soilObjectArray.length - 1])))
        });
        return soilObjectArray;
    }

    static getSoilMetabolitesByRun(run: Run, selectedProject: Project | undefined): SoilInputs[] {
        var soilObjectArray: SoilInputs[] = [];
        run.metabolites.forEach((metabolite: any) => {
            var soilMetaboliteInputs: SoilInputs = {
                jobID: run.id,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                crop: run.cropInterceptionName,
                applicationScheme: run.applicationSchemeData?.name,
                applicationRate: run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate,
                isParent: false,
                activeSubstance: run.substanceName,
                metabolite: metabolite.substanceName,
                pathway: run.dataSet?.description,
                molecularWeight: metabolite.molecularWeight ?? 0,
                parentMolecularWeight: run.molecularWeight ?? 0,
                maximumOccurance: metabolite.maximumOccurrenceInSoil ?? 0,
                koc: metabolite.kfoc ?? 0,
                degT50: metabolite.halfLifeInSoil ?? 0,
                degT90: this.calculateDegT90(metabolite.k1) ?? 0,
                k1: metabolite.k1 ?? 0,
                k2: metabolite.k2 ?? 0,
                alpha: metabolite.alpha ?? 0,
                beta: metabolite.beta ?? 0,
                tb: metabolite.tb ?? 0,
                g: metabolite.g ?? 0,
                models: metabolite.kineticModel ?? "",
                cropInterception: run.applicationSchemeData.cropInterception ?? 0,
                numApplication: run.applicationSchemeData.numberOfApplications ?? 0,
                applicationInterval: run.applicationSchemeData.applicationInterval ?? 0,
                tillage: run.applicationSchemeData.tillage,
                bbch: run.applicationSchemeData.bbchEarliest ?? "",
                initialSoilDepth: run.applicationSchemeData.soilDepth ?? 0,
                plateauSoilDepth: run.applicationSchemeData.plateauSoilDepth ?? 0,
                simulationYear: run.applicationSchemeData.simulationYear ?? 0,
                gaps: this.getGapsByRunWithoutVariableRates(run),
                model: run.model?.name,
                bbchRange: run.bbchRange ?? ""
            };
            soilObjectArray.push(soilMetaboliteInputs);
        });
        return soilObjectArray;
    }

    static calculateK1(degT50: number | undefined): number {
        if (degT50 === undefined) degT50 = 0;
        return Math.log(2) / degT50;
    }

    static calculateK2(degT50: number | undefined, degT90: number | undefined): number {
        if (degT50 === undefined) degT50 = 0;
        if (degT90 === undefined) degT90 = 0;
        return Math.log(10) / (degT90 - degT50);
    }

    static calculateG(degT90: number | undefined, degT50: number | undefined): number {
        if (degT90 === undefined) degT90 = 0;
        if (degT50 === undefined) degT50 = 0;
        return Math.log(degT90 / degT50) / Math.log(2);
    }

    static calculateTb(degT90: number | undefined, degT50: number | undefined): number {
        if (degT90 === undefined) degT90 = 0;
        if (degT50 === undefined) degT50 = 0;
        return (degT90 - degT50) / Math.log(2);
    }

    static calculateDegT50(k: number | undefined, tb: number | undefined): number {
        if (k === undefined) k = 0;
        if (tb === undefined) tb = 0;
        return k * tb;
    }

    static calculateDegT90(k1: number | undefined): number | undefined {
        if (k1 === undefined) {
            return undefined;
        }
        return Math.log(10) / k1;
    }

    static calculateAlpha(degT50: number | undefined, degT90: number | undefined): number {
        if (degT50 === undefined) degT50 = 0;
        if (degT90 === undefined) degT90 = 0;
        return (degT90 - degT50) / (Math.log(10) - Math.log(2));
    }

    static calculateBeta(alpha: number | undefined, degT50: number | undefined): number {
        if (alpha === undefined) alpha = 0;
        if (degT50 === undefined) degT50 = 0;
        return alpha * degT50;
    }

    static createStep1n2Object(runs: Run[], selectedProject: Project | undefined) {
        var step1n2InputsArray: Step1n2Inputs[] = [];
        runs.forEach(run => {
            var step1n2inputs: Step1n2Inputs = {
                runID: run.id,
                activeSubstance: run.substanceName,
                applicationInterval: run.applicationSchemeData?.applicationInterval,
                applicationRate: run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate,
                applicationScheme: run.applicationSchemeData?.name,
                applicationSchemePk: run.applicationSchemeData?.applicationSchemePk,
                crop: run.cropDriftInterceptionName,
                cropCoverage: run.applicationSchemeData?.cropCoverage,
                cropInterception: Utils.convertToRange(run.applicationSchemeData?.cropInterception),
                distanceToWaterBody: run.distanceToWater,
                DT50: run.halfLifeInSedimentWaterSystem,
                DT50Sediment: run.halfLifeinSediment,
                DT50Soil: run.halfLifeInSoil,
                DT50Water: run.halfLifeInWater,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                isParent: true,
                kocCompound: run.kfoc,
                location: run.regionAndSeason?.name,
                maxConcSoil: run.maximumOccurrenceInSoil,
                maxConcWaterSed: run.maximumOccurrenceInWaterSediment,
                metabolite: run.substanceName,
                molecularMassActive: run.molecularWeight,
                numApplications: run.applicationSchemeData?.numberOfApplications,
                pathway: run.dataSet?.description,
                waterSolubility: run.solubilityInWater,
                sprayDriftPercentage_Single: (run.drift && run.drift.length > 0) ? run.drift[0] : undefined,
                sprayDriftPercentage: (run.drift && run.drift.length > 0) ? run.drift[run.applicationSchemeData?.numberOfApplications - 1] : undefined,
                earliestBbch: run.applicationSchemeData?.bbchEarliest,
                latestBbch: run.applicationSchemeData?.bbchEarliest,
                metabolites: this.getStep1n2MetabolitesByRun(run, selectedProject),
                bbchRange: run.bbchRange,
                dataSetPk: run.dataSet?.dataSetPk,
                featurePk: run.featurePk,
            }
            step1n2InputsArray.push(step1n2inputs)
        });
        return step1n2InputsArray;
    }

    static getStep1n2MetabolitesByRun(run: any, selectedProject: Project | undefined): Step1n2Inputs[] {
        var step1n2InputsArray: Step1n2Inputs[] = [];
        run.metabolites.forEach((metabolite: any) => {
            var step1n2inputs: Step1n2Inputs = {
                runID: run.id,
                activeSubstance: run.substanceName,
                applicationInterval: run.applicationSchemeData?.applicationInterval,
                applicationRate: run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate,
                applicationScheme: run.applicationSchemeData?.name,
                applicationSchemePk: run.applicationSchemeData?.applicationSchemePk,
                crop: run.cropDriftInterceptionName,
                cropCoverage: run.applicationSchemeData?.cropCoverage,
                cropInterception: Utils.convertToRange(run.applicationSchemeData?.cropInterception),
                distanceToWaterBody: run.distanceToWater,
                DT50: metabolite.halfLifeInSedimentWaterSystem,
                DT50Sediment: metabolite.halfLifeinSediment,
                DT50Soil: metabolite.halfLifeInSoil,
                DT50SoilParent: run.halfLifeInSoil,
                DT50Water: metabolite.halfLifeInWater,
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                isParent: false,
                kocCompound: metabolite.kfoc,
                maxConcSoil: metabolite.maximumOccurrenceInSoil,
                maxConcWaterSed: metabolite.maximumOccurrenceInWaterSediment,
                metabolite: metabolite.substanceName,
                molecularMassActive: metabolite.molecularWeight,
                molecularMassCalc: metabolite.molecularWeight,
                numApplications: run.applicationSchemeData?.numberOfApplications,
                pathway: run.dataSet?.description,
                kocParentCompound: run.kfoc,
                waterSolubility: metabolite.solubilityInWater,
                sprayDriftPercentage_Single: (run.drift && run.drift.length > 0) ? run.drift[0] : undefined,
                sprayDriftPercentage: (run.drift && run.drift.length > 0) ? run.drift[run.applicationSchemeData?.numberOfApplications - 1] : undefined,
                location: run.regionAndSeason?.name,
                earliestBbch: run.applicationSchemeData?.bbchEarliest,
                latestBbch: run.applicationSchemeData?.bbchEarliest,
                bbchRange: run.bbchRange,
                dataSetPk: run.dataSet?.dataSetPk,
            }
            step1n2InputsArray.push(step1n2inputs);
        });
        return step1n2InputsArray;
    }


    static async createUKObject(runs: Run[], selectedProject: Project | undefined): Promise<UKInputs[]> {
        var UKInputsArray: UKInputs[] = [];
        return new Promise<UKInputs[]>((resolve, reject) => {
            this.cropListService.getAllCropsList().subscribe((crops: CropList[]) => {
                runs.forEach(run => {
                    var ukInputs: UKInputs = {
                        runID: run.id,
                        activeSubstance: run.substanceName,
                        applicationInterval: run.applicationSchemeData?.applicationInterval,
                        appRate: run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate,
                        applicationScheme: run.applicationSchemeData?.name,
                        modelApplicationMethod: run.applicationSchemeData?.cropSprayPk,
                        cropDriftGroup: crops.find(x => x.CropListPk == run.applicationSchemeData?.cropSprayPk)?.CropName,
                        cropDrain: crops.find(x => x.CropListPk == run.applicationSchemeData?.cropInterceptionPk)?.CropName,
                        firstApplicationDate: Utils.formatIsoDateToCustom(run.applicationSchemeData.applicationSchemeXApplicationWindow[0].firstDate),
                        cropCoverage: run.applicationSchemeData?.cropCoverage,
                        cropInterception: run.applicationSchemeData?.cropInterception,
                        soilDT50: run.halfLifeInSoil,
                        waterDT50: run.halfLifeInWater,
                        sedimentDT50: run.halfLifeinSediment,
                        BBCH: run.applicationSchemeData?.bbchEarliest,
                        formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                        isParent: true,
                        kfoc: run.kfoc,
                        maxSediment: run.maximumOccurrenceInSediment,
                        maxWater: run.maximumOccurrenceInWater,
                        metabolite: run.substanceName,
                        applicationNumber: run.applicationSchemeData?.numberOfApplications,
                        pathway: run.dataSet?.description,
                        metabolites: this.getMetabolitesByUKRun(run, selectedProject, crops),
                        molecularWeight: run.molecularWeight,
                        parentMolecularWeight: run.molecularWeight,
                        maxSoil: run.maximumOccurrenceInSoil
                    }
                    UKInputsArray.push(ukInputs)
                });
                resolve(UKInputsArray);
            })
        })
    }

    static getMetabolitesByUKRun(run: Run, selectedProject: Project | undefined, crops: CropList[]): UKInputs[] {
        var UKInputsArray: UKInputs[] = [];
        run.metabolites.forEach((metabolite: any) => {
            UKInputsArray.push({
                appRate: run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate,
                isParent: false,
                parentMolecularWeight: run.molecularWeight,
                applicationNumber: run.applicationSchemeData?.numberOfApplications,
                repeatInterval: run.applicationSchemeData?.applicationInterval ?? 0,
                substanceName: metabolite.substanceName,
                metabolite: metabolite.substanceName,
                cropDriftGroup: crops.find(x => x.CropListPk == run.applicationSchemeData?.cropSprayPk)?.CropName,
                cropDrain: crops.find(x => x.CropListPk == run.applicationSchemeData?.cropInterceptionPk)?.CropName,
                activeSubstance: run.substanceName,
                applicationDayOfYearString: [""],
                applicationScheme: run.applicationSchemeData?.name,
                applicationType: run.applicationSchemeData?.applicationMethod,
                compound: metabolite.substanceName,
                crop: run.globalCropName,
                cropPk: run.globalCropPk,
                degradationDT50: metabolite.halfLifeInSoil,
                exponent: metabolite.exponent,
                firstDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate),
                formulation: Utils.replaceSquareBracketsWithParentheses(selectedProject?.formulation),
                koc: metabolite.kfoc,
                lastDayLong: Utils.formatIsoDateToCustom(run.applicationSchemeData.endDate),
                metabolites: [],
                molecularWeight: metabolite.molecularWeight,
                cropInterception: run.applicationSchemeData?.cropInterception,
                model: run.model?.name,
                numberOfApplications: run.applicationSchemeData.numberOfApplications,
                pathway: run.dataSet?.description,
                plantUptake: run.coefficientForUptakeByPlant ?? 0,
                rainfallFile: "",
                season: run.applicationSchemeData.season,
                sorptionValue: metabolite.kfoc,
                soilDepthIncorporated: run.applicationSchemeData.soilDepth,
                vapourPressure: run.saturatedVapourPressure,
                waterSolubility: metabolite.solubilityInWater,
                precursors: metabolite.precursors,
                soilDT50: metabolite.halfLifeInSoil,
                waterDT50: metabolite.halfLifeInWater,
                sedimentDT50: metabolite.halfLifeinSediment,
                kfoc: metabolite.kfoc,
                maxSediment: metabolite.maximumOccurrenceInSediment,
                maxWater: metabolite.maximumOccurrenceInWater,
                maxSoil: metabolite.maximumOccurrenceInSoil,
                gaps: []
            } as MacroGWInputs);
        });
        return UKInputsArray;
    }

    static groupRunsByModel(runs: Run[]): { [key: string]: Run[] } {
        return runs.reduce((group: { [key: string]: Run[] }, item) => {
            if (!group[item.model!.name]) {
                group[item.model!.name] = [];
            }
            group[item.model!.name].push(item);
            return group;
        }, {});
    }

    static returnsListValuesRepeatedByNumOfApplications(numOfApplications: number, value: number): string[] {
        let intervals: string[] = [];
        for (let i = 0; i < numOfApplications; i++) {
            intervals.push(String(value));
        }
        return intervals;
    }

    static getSwashApplicationIntervals(run: Run): number[] {
        const numberOfApplications = Number(run.applicationSchemeData?.numberOfApplications ?? 0);
        if (numberOfApplications < 2) return [];
        const applications: ApplicationSchemeXApplication[] = [...(run.applicationSchemeData?.applicationSchemeXApplication ?? [])]
            .filter((application: ApplicationSchemeXApplication) => application.application_number != null)
            .sort((a: ApplicationSchemeXApplication, b: ApplicationSchemeXApplication) => Number(a.application_number) - Number(b.application_number));
        const isBackward = run.applicationSchemeData?.appTimingDirection === Constants.APP_TIMING_DIRECTIONS.BACKWARD;
        const intervals = (isBackward ? applications.slice(0, -1) : applications.slice(1))
            .map((application: ApplicationSchemeXApplication) =>
                application.min_application_interval == null ? NaN : Math.abs(Number(application.min_application_interval)));
        return applications.length === numberOfApplications && intervals.every(interval => Number.isFinite(interval))
            ? intervals
            : [];
    }

    static getDosages(run: Run): string[] {
        return run.applicationSchemeData?.hasVariableRates ?
            this.getSwashDosages(run) :
            this.returnsListValuesRepeatedByNumOfApplications(run.applicationSchemeData.numberOfApplications, run.applicationSchemeData?.applicationSchemeXActiveIngredientRate?.find((x: ApplicationSchemeXActiveIngredientRate) => x.moleculePk === run.activeIngredientPk)?.rate)
    }

    static getSwashDosages(run: Run): string[] {
        let dosages: string[] = [];
        run.applicationSchemeData.applicationSchemeXApplication.forEach((application: any) => {
            application?.applicationSchemeXActiveIngredientRate?.forEach((ai: ApplicationSchemeXActiveIngredientRate) => {
                if (ai.moleculePk === run.activeIngredientPk) {
                    dosages.push(String(ai.rate));
                }
            });
        });
        return dosages;
    }

    static getGapsByRun(run: Run): Gap[] {
        return run.applicationSchemeData?.hasVariableRates ? this.getGapsByRunWithVariableRates(run) : this.getGapsByRunWithoutVariableRates(run)
    }

    static getGapsByRunWithoutVariableRates(run: Run): Gap[] {
        let gap: Gap = {
            activeIngredientRates: [] = [],
            gapPk: 0,
            applicationSchemePk: 0,
            cropInterception: 0
        };
        gap.applicationDate = Utils.formatIsoDateToCustom(run.applicationSchemeData.firstDate);
        gap.cropGrowthStageInit = undefined;
        gap.applicationIntervalMinPk = run.applicationSchemeData.applicationInterval;
        gap.dateType = run.applicationSchemeData.dateType;
        gap.cropInterception = run.applicationSchemeData.cropInterception;
        gap.fractionIntercepted = run.applicationSchemeData.cropInterception > 0 ? (run.applicationSchemeData.cropInterception / 100).toFixed(2) : run.applicationSchemeData.cropInterception.toString();
        gap.minBBCH = run.applicationSchemeData.bbchEarliest;
        gap.bbchRange = run.bbchRange;
        gap.daysSince = run.applicationSchemeData.daysSince;
        gap.applicationInterval = run.applicationSchemeData.applicationInterval;
        gap.rate = run.applicationSchemeData.applicationSchemeXActiveIngredientRate?.find((ai: ApplicationSchemeXActiveIngredientRate) => ai.moleculePk === run.activeIngredientPk)?.rate;
        let ais: ActiveIngredientRate[] = []
        run.applicationSchemeData.applicationSchemeXActiveIngredientRate.filter((ai: ApplicationSchemeXActiveIngredientRate) => run.activeIngredientPk == ai.moleculePk).forEach((AI: ApplicationSchemeXActiveIngredientRate) => {
            if (AI.moleculePk === run.activeIngredientPk) {
                ais.push({
                    activeIngredientRateXGapPk: 0,
                    activeIngredientPk: AI.moleculePk,
                    max: AI.rate,
                    activeIngredientName: AI.substanceName,
                });
            }
        });
        gap.activeIngredientRates = ais;
        let gaps: Gap[] = [gap];
        let interval = 0;
        for (let i = 1; i < run.applicationSchemeData.numberOfApplications; i++) {
            let otherGap: Gap = JSON.parse(JSON.stringify(gap))
            interval += run.applicationSchemeData.applicationInterval;
            otherGap.applicationDate = Utils.addDaysToDateFormated(gap.applicationDate, interval);
            gaps.push(otherGap);
        }
        return gaps;
    }

    static getGapsByRunWithVariableRates(run: Run): Gap[] {
        let gaps: Gap[] = [];
        let interval = 0;
        let applications = run.applicationSchemeData?.applicationSchemeXApplication?.sort((a: any, b: any) => {
            return a.application_number - b.application_number;
        }) ?? [];
        applications.forEach((application: any) => {
            let gap: Gap = {
                activeIngredientRates: [] = [],
                gapPk: 0,
                applicationSchemePk: 0,
                cropInterception: 0
            };
            gap.applicationDate = this.getVariableRateDate(run, application);
            gap.cropGrowthStageInit = undefined;
            gap.applicationIntervalMinPk = interval + application.min_application_interval;
            gap.dateType = run.applicationSchemeData.dateType;
            gap.cropInterception = application.crop_interception;
            gap.fractionIntercepted = application.crop_interception > 0 ? (application.crop_interception / 100).toFixed(2) : application.crop_interception.toString();
            gap.minBBCH = application.earliest_bbch;
            gap.bbchRange = application.bbchRange;
            gap.applicationInterval = application.applicationInterval;
            gap.daysSince = application.days_since;
            gap.rate = application.applicationSchemeXActiveIngredientRate?.find((ai: ApplicationSchemeXActiveIngredientRate) => ai.moleculePk === run.activeIngredientPk)?.rate;
            let ais: ActiveIngredientRate[] = []
            application.applicationSchemeXActiveIngredientRate.filter((ai: ApplicationSchemeXActiveIngredientRate) => run.activeIngredientPk == ai.moleculePk).forEach((AI: ApplicationSchemeXActiveIngredientRate) => {
                if (AI.moleculePk === run.activeIngredientPk) {
                    ais.push({
                        activeIngredientRateXGapPk: 0,
                        activeIngredientPk: AI.moleculePk,
                        max: AI.rate,
                        activeIngredientName: AI.substanceName,
                    });
                }
            });
            gap.activeIngredientRates = ais;
            gaps.push(gap);
            interval += application.min_application_interval;
        });
        return gaps;
    }

    static getVariableRateDate(run: Run, application: any): string {
        let location = run.applicationSchemeData.location;
        let date = application.applicationSchemeXApplicationWindow.find((date: any) => date.location === location)?.firstDate;
        return Utils.formatIsoDateToCustom(date);
    }

    static setApplicationSchemeCropState(data: any, geographiesList: Catalog[]): ApplicationSchemeCropState[] {
        let result: ApplicationSchemeCropState[] = [];
        data?.forEach((x: any) => {
            let applicationSchemeCropState: ApplicationSchemeCropState = {
                applicationSchemePk: x.applicationSchemeData.applicationSchemePk,
                crop: x?.globalCropName,
                state: geographiesList.find(reg => reg.key === x?.applicationSchemeData?.regionPk)?.name ?? ''
            };
            result.push(applicationSchemeCropState);
        });
        return result;
    }

    static getFormationFractionByKey(precursors: FractionTransformedXEndpoint[], fractionName: string, selectedProject: Project | undefined) {
        if (!precursors)
            return [{ formationFraction: 0, precursor: "" }];

        let precursorsFiltered = precursors?.filter(precursor => precursor.precursorType === fractionName)?.map((x: FractionTransformedXEndpoint) => ({
            formationFraction: x.formationFraction,
            precursor: this.getPearlAlternativeName(x, selectedProject?.projectXCompoundXModel)
        }));

        return precursorsFiltered?.length > 0 ? precursorsFiltered : [{ formationFraction: 0, precursor: "" }];
    }

    static getSwashCropInterception(useSwashCropInterception: any, cropDriftInterceptionPk: any, bbchEarliest: any): number | undefined {
        if (!useSwashCropInterception) {
            let cropInterception = this.cropInterceptions.find((x: CropInterception) => x.cropListPk === cropDriftInterceptionPk && (x.minBbch <= bbchEarliest && x.maxBbch >= bbchEarliest));
            if (cropInterception) {
                return cropInterception.interceptionPercentage;
            }
        }
        return undefined;
    }

    static calculateRateMetaboliteAsParent(runs: Run[]): void {
      runs.forEach(run => {
        const appSchemeRates = run.applicationSchemeData?.applicationSchemeXActiveIngredientRate;
        if (!appSchemeRates?.length) {
          return;
        }

        const metaboliteRateFactor = run.groupedEndpointFieldValues?.find(
          (endpointField: GroupedEndpointFieldValue) =>
            endpointField.key?.toLowerCase() === Constants.DATA_VALUES_NAMES.metaboliteAppliedRateFactor
        );

        if (metaboliteRateFactor?.value != null) {
          appSchemeRates.forEach((appSchemeRate: ApplicationSchemeXActiveIngredientRate) => {
            if (appSchemeRate.rate) {
              appSchemeRate.rate = appSchemeRate.rate * metaboliteRateFactor.value;
            }
          });
        }
      });
    }
}
